import {
  Breadcrumbs,
  Button,
  Card,
  PageContainer,
  useApiClient,
  useStatCount,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow, format } from 'date-fns';
import { CalendarClock, Hammer, MapPin, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { DashboardEventRow } from '@/components/events/DashboardEventRow';
import type { Event } from '@/config/objects/events';

const SECTION_LIMIT = 5;

interface EventListResponse {
  data: Event[];
  pagination?: { total?: number };
}

/** Fetches events by status via the standard list endpoint. */
function useEventsByStatus(statuses: readonly string[], orderBy: Record<string, 'asc' | 'desc'>) {
  const apiClient = useApiClient();
  return useQuery<EventListResponse>({
    queryKey: ['Event', 'dashboard', statuses],
    queryFn: () =>
      apiClient.post('/events/list', {
        page: 1,
        limit: SECTION_LIMIT,
        where: { status: statuses.length === 1 ? statuses[0] : { in: [...statuses] } },
        orderBy,
      }),
  });
}

/** Trail-sign style stat: display-serif figure over a mono wayfinding label. */
function TrailStat({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="border-l-2 border-primary/50 pl-4">
      <p className="font-display text-4xl font-medium tracking-tight text-foreground tabular-nums">
        {value}
      </p>
      <p className="mt-1 font-mono text-[0.6875rem] tracking-[0.15em] text-muted-foreground uppercase">
        {label}
      </p>
    </div>
  );
}

function SectionCard({
  title,
  description,
  emptyText,
  emptyAction,
  events,
  metaFor,
}: {
  title: string;
  description: string;
  emptyText: string;
  emptyAction?: React.ReactNode;
  events: Event[] | undefined;
  metaFor: (event: Event) => string | undefined;
}) {
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="border-b px-5 py-3.5">
        <h2 className="text-lg font-medium tracking-tight text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {events && events.length > 0 ? (
        <div className="divide-y">
          {events.map((event) => (
            <DashboardEventRow key={event.id} event={event} meta={metaFor(event)} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 px-5 py-10 text-center">
          <MapPin className="h-5 w-5 text-muted-foreground/60" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">{emptyText}</p>
          {emptyAction}
        </div>
      )}
    </Card>
  );
}

export function DashboardPage() {
  const navigate = useNavigate();
  const breadcrumbs = [{ label: 'Dashboard', current: true }];

  const active = useEventsByStatus(['active', 'paused'], { updatedAt: 'desc' });
  const upcoming = useEventsByStatus(['scheduled'], { startTime: 'asc' });
  const drafts = useEventsByStatus(['draft'], { updatedAt: 'desc' });
  const challengeCount = useStatCount('/challenges', ['Challenge', 'stat-count']);

  const teamsPlaying = (active.data?.data ?? []).reduce(
    (sum, event) => sum + (event._count?.teams ?? 0),
    0,
  );

  return (
    <PageContainer>
      <Breadcrumbs items={breadcrumbs} className="mb-4" />

      <div className="space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Everything happening across your hunts, at a glance.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/challenges/new')}>
              <Plus className="h-4 w-4" /> New Challenge
            </Button>
            <Button onClick={() => navigate('/events/new')}>
              <Plus className="h-4 w-4" /> New Event
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-8 gap-y-6 lg:grid-cols-4">
          <TrailStat value={active.data?.pagination?.total ?? '—'} label="Hunts live now" />
          <TrailStat value={teamsPlaying} label="Teams playing" />
          <TrailStat value={upcoming.data?.pagination?.total ?? '—'} label="Upcoming hunts" />
          <TrailStat
            value={challengeCount.data?.pagination?.total ?? '—'}
            label="Library challenges"
          />
        </div>

        <div className="grid gap-6 xl:grid-cols-2">
          <SectionCard
            title="Live now"
            description="Active and paused hunts."
            emptyText="No hunts are running right now."
            events={active.data?.data}
            metaFor={(event) =>
              event.startedAt
                ? `Started ${formatDistanceToNow(new Date(event.startedAt), { addSuffix: true })}`
                : undefined
            }
          />
          <SectionCard
            title="Up next"
            description="Scheduled and ready to start."
            emptyText="Nothing scheduled yet."
            events={upcoming.data?.data}
            metaFor={(event) =>
              event.startTime ? `Starts ${format(new Date(event.startTime), 'MMM d, h:mm a')}` : undefined
            }
          />
        </div>

        <SectionCard
          title="Continue building"
          description="Drafts, most recently edited first."
          emptyText="No drafts in progress — start a new hunt."
          emptyAction={
            <Button size="sm" variant="outline" onClick={() => navigate('/events/new')}>
              <Hammer className="h-4 w-4" /> New Event
            </Button>
          }
          events={drafts.data?.data}
          metaFor={(event) =>
            event.updatedAt
              ? `Edited ${formatDistanceToNow(new Date(event.updatedAt), { addSuffix: true })}`
              : undefined
          }
        />

        {upcoming.data?.data?.length ? (
          <p className="flex items-center gap-2 font-mono text-xs tracking-wide text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
            Times shown in your local timezone.
          </p>
        ) : null}
      </div>
    </PageContainer>
  );
}
